import React, { memo, useMemo, useState } from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, CircularProgress, Alert, Accordion, AccordionSummary, AccordionDetails,
  useMediaQuery, useTheme, Chip, FormControlLabel, Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InvButton from '../InvButton';
import { formatCurrency, parseMoney } from '../../utils';
import { resolveRfqVendorLabel, invitationVendorId } from '../../utils/rfqEntity';
import { rankQuotes, perItemLowest, splitAwardSummary, otherChargesAmount } from '../../utils/rfqComparison';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { IPD_COLORS } from '../../theme';

const L1_CELL_SX = {
  backgroundColor: IPD_COLORS.success.bg,
  color: IPD_COLORS.success.text,
  fontWeight: 700,
};

/** "L1" as text, not just a green cell — colour alone must not carry the meaning. */
function L1Tag() {
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75, px: 0.5, borderRadius: '4px', fontSize: 10, fontWeight: 700,
        border: `1px solid ${IPD_COLORS.success.text}`, color: IPD_COLORS.success.text,
      }}
    >
      L1
    </Box>
  );
}

function RankChip({ quote }) {
  if (!quote) return null;
  // A partial quote's rank is not a like-for-like win, so it stays neutral.
  const isWinner = quote.rank === 1 && !quote.partial;
  return (
    <Chip
      size="small"
      label={quote.rankLabel}
      sx={{
        height: 18, fontSize: 10, fontWeight: 700,
        backgroundColor: isWinner ? IPD_COLORS.success.text : IPD_COLORS.selectedBg,
        color: isWinner ? '#fff' : IPD_COLORS.textSecondary,
      }}
    />
  );
}

function PartialChip({ quote }) {
  if (!quote?.partial) return null;
  return (
    <Chip
      size="small"
      label={`Partial — ${quote.quotedCount} of ${quote.itemCount}`}
      sx={{
        height: 18, fontSize: 10, fontWeight: 600,
        backgroundColor: IPD_COLORS.warning.bg, color: IPD_COLORS.warning.text,
      }}
    />
  );
}

function SplitAwardCard({ split, vendorMap, onUseSplit, onAwardL1 }) {
  if (!split || split.itemsCovered === 0) return null;

  let savingLine;
  if (!split.hasCompleteQuote) {
    savingLine = 'No vendor quoted every item, so there is no complete quote to compare this against.';
  } else if (split.saving > 0) {
    savingLine = `Saves ${formatCurrency(split.saving)} (${split.savingPct.toFixed(1)}%) compared with awarding the whole RFQ to L1.`;
  } else if (split.saving === 0) {
    savingLine = 'Same as the L1 quote — that vendor is already lowest on every item.';
  } else {
    savingLine = `Splitting costs ${formatCurrency(Math.abs(split.saving))} more than the L1 quote — a full award is cheaper here.`;
  }

  return (
    <Box sx={{
      mt: 2.5, p: 2, borderRadius: '10px',
      border: `1px solid ${IPD_COLORS.border}`,
      borderLeft: `4px solid ${IPD_COLORS.success.text}`,
      backgroundColor: IPD_COLORS.cardBg,
    }}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
        Item-wise lowest: {formatCurrency(split.splitTotal)}
      </Typography>
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.5 }}>
        {savingLine}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
        {split.byVendor.map((v) => (
          <Chip
            key={v.vendorId}
            size="small"
            label={`${vendorMap[v.vendorId] || v.vendorId}: ${v.items.length} ${v.items.length === 1 ? 'item' : 'items'} · ${formatCurrency(v.value)}`}
            sx={{ backgroundColor: IPD_COLORS.selectedBg, color: IPD_COLORS.textPrimary, fontSize: 11 }}
          />
        ))}
      </Box>

      {(onAwardL1 || onUseSplit) && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {onAwardL1 && (
            <InvButton size="small" tone="mint" onClick={onAwardL1}>
              Award L1
            </InvButton>
          )}
          {onUseSplit && (
            <InvButton size="small" tone="mint" variant="outlined" onClick={onUseSplit}>
              Use split award
            </InvButton>
          )}
        </Box>
      )}
    </Box>
  );
}

function ComparisonDesktopTable({
  items, ranked, vendorMap, resolveItemLabel, lowestByItem, highlightL1, showUnitRate,
}) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table
        size="small"
        sx={{
          minWidth: 640,
          '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                color: IPD_COLORS.textSecondary, minWidth: 180,
                position: 'sticky', left: 0, zIndex: 2, backgroundColor: IPD_COLORS.cardBg,
              }}
            >
              Item
            </TableCell>
            <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
            {ranked.map((q) => (
              <TableCell key={q.quoteId} align="right" sx={{ color: IPD_COLORS.textSecondary, minWidth: 130 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.4 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
                    {vendorMap[q.vendor_id] || q.vendor_id}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <RankChip quote={q} />
                    <PartialChip quote={q} />
                  </Box>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {items.map((row) => {
            const lowest = lowestByItem[row.rfq_item_id];
            return (
              <TableRow key={row.rfq_item_id || row.item_id}>
                <TableCell sx={{
                  position: 'sticky', left: 0, zIndex: 1,
                  backgroundColor: IPD_COLORS.cardBg, fontWeight: 600,
                }}>
                  {resolveItemLabel(row)}
                </TableCell>
                <TableCell align="right">{row.requested_quantity}</TableCell>
                {ranked.map((q) => {
                  const line = (row.quotes || []).find((l) => l.vendor_quote_id === q.quoteId);
                  if (!line) {
                    return (
                      <TableCell
                        key={q.quoteId}
                        align="right"
                        sx={{ fontStyle: 'italic', color: IPD_COLORS.textSecondary, backgroundColor: IPD_COLORS.selectedBg }}
                      >
                        Not quoted
                      </TableCell>
                    );
                  }
                  const isL1 = highlightL1 && lowest?.vendor_quote_id === q.quoteId;
                  return (
                    <TableCell key={q.quoteId} align="right" sx={isL1 ? L1_CELL_SX : undefined}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        {showUnitRate ? formatCurrency(line.unit_rate) : formatCurrency(line.line_total)}
                        {isL1 && <L1Tag />}
                      </Box>
                      {!showUnitRate && parseMoney(line.discount_percent) > 0 && (
                        <Typography sx={{ fontSize: 11, color: isL1 ? IPD_COLORS.success.text : IPD_COLORS.textSecondary }}>
                          less {parseMoney(line.discount_percent)}%
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}

          {[
            { label: 'Basic', field: 'total_basic_amount' },
            { label: 'Discount', field: 'total_discount_amount' },
            { label: 'Tax', field: 'total_tax_amount' },
          ].map((r) => (
            <TableRow key={r.field} sx={{ backgroundColor: IPD_COLORS.selectedBg }}>
              <TableCell sx={{
                position: 'sticky', left: 0, zIndex: 1,
                backgroundColor: IPD_COLORS.selectedBg, color: IPD_COLORS.textSecondary,
              }}>
                {r.label}
              </TableCell>
              <TableCell />
              {ranked.map((q) => (
                <TableCell key={q.quoteId} align="right" sx={{ color: IPD_COLORS.textSecondary }}>
                  {formatCurrency(q[r.field])}
                </TableCell>
              ))}
            </TableRow>
          ))}

          <TableRow sx={{ backgroundColor: IPD_COLORS.selectedBg }}>
            <TableCell sx={{
              position: 'sticky', left: 0, zIndex: 1,
              backgroundColor: IPD_COLORS.selectedBg, color: IPD_COLORS.textSecondary,
            }}>
              Freight and other
            </TableCell>
            <TableCell />
            {ranked.map((q) => (
              <TableCell key={q.quoteId} align="right" sx={{ color: IPD_COLORS.textSecondary }}>
                {formatCurrency(otherChargesAmount(q))}
              </TableCell>
            ))}
          </TableRow>

          <TableRow sx={{ backgroundColor: IPD_COLORS.selectedBg }}>
            <TableCell sx={{
              position: 'sticky', left: 0, zIndex: 1,
              backgroundColor: IPD_COLORS.selectedBg, fontWeight: 700,
            }}>
              Net total
            </TableCell>
            <TableCell />
            {ranked.map((q) => {
              const isWinner = q.rank === 1 && !q.partial;
              return (
                <TableCell
                  key={q.quoteId}
                  align="right"
                  sx={{ fontWeight: 700, color: isWinner ? IPD_COLORS.success.text : IPD_COLORS.textPrimary }}
                >
                  {formatCurrency(q.net)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

function ComparisonMobileAccordions({ items, ranked, vendorMap, resolveItemLabel, lowestByItem }) {
  const rankById = useMemo(() => {
    const map = {};
    ranked.forEach((q) => { map[q.quoteId] = q; });
    return map;
  }, [ranked]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map((row, i) => {
        const lowest = lowestByItem[row.rfq_item_id];
        return (
          <Accordion
            key={row.rfq_item_id || row.item_id || i}
            disableGutters
            sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: '8px !important', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{resolveItemLabel(row)}</Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>Qty {row.requested_quantity}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {(row.quotes || []).length === 0 ? (
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>No quotes for this item.</Typography>
              ) : (row.quotes || []).map((q) => {
                const isL1 = lowest?.vendor_quote_id === q.vendor_quote_id;
                return (
                  <Box key={q.vendor_quote_id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: 13 }}>{vendorMap[q.vendor_id] || q.vendor_id}</Typography>
                      <RankChip quote={rankById[q.vendor_quote_id]} />
                    </Box>
                    <Typography sx={{
                      fontSize: 13,
                      fontWeight: isL1 ? 700 : 400,
                      color: isL1 ? IPD_COLORS.success.text : IPD_COLORS.textPrimary,
                    }}>
                      {formatCurrency(q.line_total)}{isL1 ? ' · L1' : ''}
                    </Typography>
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
      {ranked.map((q) => (
        <Box
          key={q.quoteId}
          sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            px: 1.5, py: 1, border: `1px solid ${IPD_COLORS.border}`, borderRadius: '8px',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{vendorMap[q.vendor_id] || q.vendor_id}</Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              Freight and other {formatCurrency(otherChargesAmount(q))}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {formatCurrency(q.net)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function RfqQuoteComparisonTable({
  comparison,
  loading,
  error,
  vendorDirectory = [],
  invitations = [],
  resolveItem,
  onUseSplit,
  onAward,
  onUnseal,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [highlightL1, setHighlightL1] = useState(true);
  const [showUnitRate, setShowUnitRate] = useState(false);

  const items = comparison?.items || [];
  const ranked = useMemo(() => rankQuotes(comparison), [comparison]);
  const lowestByItem = useMemo(() => perItemLowest(comparison), [comparison]);
  const split = useMemo(() => splitAwardSummary(comparison, ranked), [comparison, ranked]);

  const vendorMap = useMemo(() => {
    const map = {};
    const ids = new Set();
    (vendorDirectory || []).forEach((v) => { if (v.vendor_id || v.id) ids.add(v.vendor_id || v.id); });
    (invitations || []).forEach((inv) => { const id = invitationVendorId(inv); if (id) ids.add(id); });
    (comparison?.vendor_quotes || []).forEach((q) => { if (q.vendor_id) ids.add(q.vendor_id); });
    ids.forEach((id) => {
      map[id] = resolveRfqVendorLabel(id, {
        vendor: (vendorDirectory || []).find((v) => (v.vendor_id || v.id) === id),
        invitation: (invitations || []).find((inv) => invitationVendorId(inv) === id),
        quote: (comparison?.vendor_quotes || []).find((q) => q.vendor_id === id),
      });
    });
    return map;
  }, [vendorDirectory, invitations, comparison]);

  function resolveItemLabel(row) {
    if (resolveItem) {
      return resolveItem({ item_id: row.item_id, id: row.rfq_item_id }) || row.item_id || '—';
    }
    return row.item_id || '—';
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // The service withholds every rate while a sealed RFQ is still closed, so
  // there is nothing to rank or compare — say so rather than drawing an empty
  // matrix or, worse, zeroes.
  if (comparison && comparison.rates_visible === false) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <LockOutlinedIcon sx={{ fontSize: 40, color: IPD_COLORS.textSecondary }} />
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: IPD_COLORS.textPrimary, mt: 1 }}>
          {ranked.length} {ranked.length === 1 ? 'quote' : 'quotes'} received — rates are sealed
        </Typography>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.5, maxWidth: 460, mx: 'auto' }}>
          Rates stay hidden until the submission deadline passes. Unsealing early is
          recorded against this RFQ with the reason given.
        </Typography>
        {onUnseal && (
          <Box sx={{ mt: 2 }}>
            <InvButton size="small" tone="amber" variant="outlined" onClick={onUnseal}>
              Unseal now
            </InvButton>
          </Box>
        )}
      </Box>
    );
  }

  if (!items.length || !ranked.length) {
    return (
      <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, py: 2, textAlign: 'center' }}>
        No comparison data available.
      </Typography>
    );
  }

  const l1Quote = ranked.find((q) => q.rank === 1 && !q.partial);

  const partialCount = ranked.filter((q) => q.partial).length;

  return (
    <Box>
      {partialCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {partialCount === 1 ? 'One quote does not' : `${partialCount} quotes do not`} cover every item.
          A net total that leaves items out is not comparable, so those quotes are ranked after the complete ones.
        </Alert>
      )}

      {!isMobile && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={highlightL1}
                onChange={(e) => setHighlightL1(e.target.checked)}
                sx={{ color: IPD_COLORS.primary }}
              />
            )}
            label={<Typography sx={{ fontSize: 13 }}>Highlight lowest per item</Typography>}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={showUnitRate}
                onChange={(e) => setShowUnitRate(e.target.checked)}
                sx={{ color: IPD_COLORS.primary }}
              />
            )}
            label={<Typography sx={{ fontSize: 13 }}>Show unit rate instead of line total</Typography>}
          />
        </Box>
      )}

      {isMobile ? (
        <ComparisonMobileAccordions
          items={items}
          ranked={ranked}
          vendorMap={vendorMap}
          resolveItemLabel={resolveItemLabel}
          lowestByItem={lowestByItem}
        />
      ) : (
        <ComparisonDesktopTable
          items={items}
          ranked={ranked}
          vendorMap={vendorMap}
          resolveItemLabel={resolveItemLabel}
          lowestByItem={lowestByItem}
          highlightL1={highlightL1}
          showUnitRate={showUnitRate}
        />
      )}

      <SplitAwardCard
        split={split}
        vendorMap={vendorMap}
        onUseSplit={onUseSplit}
        onAwardL1={onAward && l1Quote ? () => onAward(l1Quote.quoteId) : undefined}
      />
    </Box>
  );
}

export default memo(RfqQuoteComparisonTable);
